import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ContentWorkflow, WorkflowState } from '@amzn/awscat-pattern-library-service-model';

import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { OpenedEdits } from '../../services/InlineEditsManager';
import { QualityCheckScanResults } from '../../services/QualityCheckService';
import { useQualityCheckState } from '../v2-hooks/useQualityCheckState';
import { useWorkflow } from '../v2-hooks/useWorkflow';
import { useLoadedContentId } from '../v2-hooks/useLoadedContentId';
import { log } from '../../loggers/Logger';
import { ContentValidationIssue, useSaveContent } from '../v2-hooks/useSaveContent';
import {
  ScannerFindingsSummary,
  ScanStage,
  computeScannerFindings,
} from '../../services/ScannerFindings';
import { IMplServiceClient } from '../../api/IMplServiceClient';

export interface AuthorPanelSharedState {
  readonly busyAction: string | null;
  readonly isBusy: boolean;
  readonly isSaveLoading: boolean;
  readonly isScanning: boolean;
  readonly isLoadingFindings: boolean;
  readonly scannerFindings: readonly ScannerFindingsSummary[];
  readonly scanResults: QualityCheckScanResults | null;
  readonly openedEdits: OpenedEdits | undefined;
  readonly errorMessage: string | undefined;
  readonly validationIssues: readonly ContentValidationIssue[] | undefined;
  readonly dismissError: () => void;
  readonly saveAndClose: () => void;
  readonly setBusyAction: (action: string | null) => void;
  readonly setErrorMessage: (message: string | undefined) => void;
  readonly setValidationIssues: (issues: readonly ContentValidationIssue[] | undefined) => void;
  readonly navigateToViewer: () => void;
  readonly workflow: ContentWorkflow | undefined;
  readonly contentId: string | undefined;
  readonly mpl: IMplServiceClient;
}

export function useAuthorPanelSharedState(expectedState?: WorkflowState): AuthorPanelSharedState {
  const { saveAndClose: saveContent } = useSaveContent();
  const mpl = useMplServiceClient();
  const workflow = useWorkflow();
  const history = useHistory();
  const contentId = useLoadedContentId();
  const {
    state: scanState,
    results: scanResults,
    openedEdits,
  } = useQualityCheckState({
    expectedState,
    workflow,
    contentId,
  });
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [validationIssues, setValidationIssues] = useState<
    readonly ContentValidationIssue[] | undefined
  >(undefined);

  const dismissError = useCallback(() => {
    setErrorMessage(undefined);
    setValidationIssues(undefined);
  }, []);

  const navigateToViewer = useCallback(() => {
    // When no content is loaded yet, then there is nowhere to navigate to.
    if (!contentId) {
      log.warn('Cannot navigate to viewer: content not loaded');
      return;
    }
    history.push(`/view/${contentId}?mode=draft`);
  }, [history, contentId]);

  const saveAndClose = useCallback(async () => {
    // When no content is loaded yet, then there is nothing to save.
    if (!contentId) {
      log.warn('Cannot save and close: content not loaded');
      return;
    }
    setBusyAction('save');
    setErrorMessage(undefined);
    setValidationIssues(undefined);
    const result = await saveContent();
    if (result.ok) {
      navigateToViewer();
    } else {
      // The save layer already composes a full, user-facing message — surface it as-is.
      setErrorMessage(result.errorMessage);
      setValidationIssues(result.validationIssues);
    }
    setBusyAction(null);
  }, [saveContent, contentId, navigateToViewer]);

  const isBusy = busyAction !== null;
  const isScanning = scanState === 'scan_in_progress';
  const isLoadingFindings = scanState === 'loading_results';
  const scanStage: ScanStage =
    expectedState === WorkflowState.PUBLISHER_REVIEW ? 'publisherReview' : 'other';

  const scannerFindings = useMemo(
    () =>
      scanResults
        ? computeScannerFindings({
            inlineEdits: openedEdits?.edits ?? [],
            scanComments: scanResults.scanComments ?? [],
            scanErrors: openedEdits?.errors ?? [],
            stage: scanStage,
          })
        : [],
    [scanResults, openedEdits, scanStage],
  );

  return {
    busyAction,
    isBusy,
    isSaveLoading: busyAction === 'save',
    isScanning,
    isLoadingFindings,
    scannerFindings,
    scanResults,
    openedEdits,
    errorMessage,
    validationIssues,
    dismissError,
    saveAndClose,
    setBusyAction,
    setErrorMessage,
    setValidationIssues,
    navigateToViewer,
    workflow,
    contentId,
    mpl,
  };
}
